/**
 * Companion body actions — the catalog of things a companion can be asked to DO with its body
 * (wave, flip someone off, facepalm, get furious, dance, fall down), and the matcher that turns
 * what a person said into one.
 *
 * Shared by signaling (which hears the room and decides) and web-next (which plays the clip), so
 * an action id means the same clip on both sides. Delivered as `COMPANION_ANIMATION_TRIGGER` with
 * `triggerType: 'action'`, per-companion `assignments` and `scope: 'targeted'`.
 *
 * Deterministic on purpose: a command must move the body within a beat of being said, and the
 * companion's LLM reply is seconds away. An LLM action tag can feed the same catalog later for
 * actions the companion chooses itself.
 *
 * A clip may not be in the library yet. The command still matches and is broadcast; the client's
 * `playAction` reports that the clip is missing and nothing moves. Content lands independently.
 */

export type CompanionActionId = 'wave' | 'flip_off' | 'facepalm' | 'furious' | 'dance' | 'fall';

/**
 * How much of the body the action needs in shot. Surfaces that frame a bust (the standard room's
 * companion view: crown to mid-chest, ~5 cm of headroom) widen for anything bigger while it plays.
 */
export type CompanionActionFraming = 'bust' | 'upper' | 'full';

/**
 * A finger shape held over part of an action's clip (web-next `handPose.ts`): from `fromSec`
 * after the clip starts until `untilEndSec` before it ends, eased in and out.
 */
export interface CompanionActionHandPose {
    pose: 'middle_finger' | 'fist' | 'point' | 'open';
    sides: Array<'l' | 'r'>;
    fromSec: number;
    untilEndSec: number;
}

export interface CompanionActionDef {
    id: CompanionActionId;
    /** Clip name in the shared animation library (`mh-animations-{male,female}.glb`). */
    clip: string;
    /**
     * Interchangeable takes of the same action, `clip` among them. The server picks one per
     * trigger (pickActionClip), so every viewer sees the same take and a repeat looks different.
     */
    variants?: string[];
    reactionClass: 'big' | 'small';
    cameraMode: 'wide_required' | 'wide_preferred' | 'dolly_push_in';
    framing: CompanionActionFraming;
    /** Minimum gap between two of these on one companion, ms — a repeated command is not a loop. */
    cooldownMs: number;
    /** Finger shape the clip does not carry itself (no clip in the inventory extends the middle finger). */
    handPose?: CompanionActionHandPose;
}

export const COMPANION_ACTIONS: Record<CompanionActionId, CompanionActionDef> = {
    // 'upper': Wave_3 waves beside the head, and its raise swings the arm out wider than the bust frame.
    wave: {
        id: 'wave', clip: 'Wave', reactionClass: 'small', cameraMode: 'wide_preferred', framing: 'upper', cooldownMs: 4000,
        // The Wave clip is a Manny retarget whose fingers splay and bend unevenly on a MetaHuman
        // hand (panda, 2026-11-04); hold a relaxed open hand over the wave instead.
        handPose: { pose: 'open', sides: ['r'], fromSec: 0.15, untilEndSec: 0.3 },
    },
    flip_off: {
        // 'upper': the clip lifts the hand to chest height, below the bust frame's lower edge,
        // so the finger played out of shot (panda, 2026-11-04).
        id: 'flip_off', clip: 'MiddleFinger', reactionClass: 'small', cameraMode: 'wide_preferred', framing: 'upper', cooldownMs: 4000,
        handPose: { pose: 'middle_finger', sides: ['r'], fromSec: 0.3, untilEndSec: 0.3 },
    },
    facepalm: { id: 'facepalm', clip: 'Facepalm', reactionClass: 'small', cameraMode: 'wide_preferred', framing: 'bust', cooldownMs: 4000 },
    furious: {
        id: 'furious', clip: 'Furious', reactionClass: 'big', cameraMode: 'wide_required', framing: 'upper', cooldownMs: 8000,
        handPose: { pose: 'fist', sides: ['l', 'r'], fromSec: 0.2, untilEndSec: 0.3 },
    },
    // Three windows of the Avaturn Dance/Dance4 takes (mh-to-glb-anim-pipe stage 05).
    dance: {
        id: 'dance', clip: 'Dance', variants: ['Dance', 'Dance2', 'Dance3'],
        reactionClass: 'big', cameraMode: 'wide_required', framing: 'full', cooldownMs: 10000,
    },
    fall: { id: 'fall', clip: 'Fall', reactionClass: 'big', cameraMode: 'dolly_push_in', framing: 'full', cooldownMs: 12000 },
};

/** The clip to play for one trigger of `def`: one of its variants, else its clip. `rand` is in [0, 1). */
export function pickActionClip(def: CompanionActionDef, rand: number = Math.random()): string {
    const v = def.variants;
    if (!v?.length) return def.clip;
    return v[Math.min(v.length - 1, Math.floor(rand * v.length))];
}

export interface CompanionActionCommand {
    action: CompanionActionId;
    /**
     * The word the command was addressed to ("jen" in "Jen, wave"), lower-case, or null when it
     * was addressed to nobody in particular. The caller decides whether that is itself.
     */
    addressee: string | null;
}

/** Words that sit where a name could, but are not one. */
const NOT_A_NAME = new Set([
    'please', 'now', 'ok', 'okay', 'so', 'and', 'just', 'hey', 'yo', 'hi', 'hello', 'oh', 'well',
    'everyone', 'everybody', 'guys', 'all', 'you', 'come', 'on', 'go', 'ahead', 'then', 'again',
    'can', 'could', 'would', 'will', 'u', 'pls', 'plz', 'lets', "let's", 'let', 'us', 'me', 'also',
    'the', 'right', 'alright', 'cool', 'great', 'nice', 'yeah', 'yes',
]);

/** Negations that turn an imperative into its opposite ("don't dance", "never flip me off"). */
const NEGATION = /\b(?:don'?t|do not|dont|never|not|no need to|stop|quit|won'?t|didn'?t|can'?t|cannot)\b/;

const DETERMINER = new Set(['a', 'an', 'the', 'my', 'your', 'his', 'her', 'our', 'their', 'this', 'that', 'its', 'some', 'any', 'one']);

/** What may precede a spoken imperative in the same clause. */
const LEAD_IN = '(?:(?:please|pls|now|ok|okay|so|and|just|hey|yo|come on|go ahead and|'
    + 'can you|could you|would you|will you|can u|why don\'t you|lets see you|let\'s see you|'
    + 'let\'s|lets|i want you to|i need you to)\\s+)*';

const WHO = '(?:me|us|him|her|them|everyone|everybody|the\\s+(?:camera|audience|crowd|chat|room|people))';

interface ActionPhrases {
    /** The verb phrase as an imperative — only matched at the start of a clause. */
    imperative: string;
    /** Phrases unambiguous enough to count anywhere in the utterance. */
    anywhere?: RegExp[];
    /** Cheap prefilter: skip the regexes unless one of these substrings is present. */
    hints: string[];
}

const PHRASES: Record<CompanionActionId, ActionPhrases> = {
    wave: {
        imperative: 'wave\\b(?!\\s+(?:of|length|lengths|form|forms|pool|function|file|machine|power|'
            + 'energy|rider|riders|pattern|patterns|goodbye\\s+to\\s+(?:the|my|our|your)))',
        anywhere: [
            new RegExp(`\\bwave\\s+(?:at|to)\\s+${WHO}\\b`),
            new RegExp(`\\bgive\\s+${WHO}\\s+a\\s+(?:little\\s+|quick\\s+|big\\s+)?wave\\b`),
            /\b(?:say\s+(?:hi|hello|bye|goodbye)|wave)\s+with\s+your\s+hand\b/,
        ],
        hints: ['wave', 'with your hand'],
    },
    flip_off: {
        imperative: `flip\\s+(?:${WHO}|[a-z][a-z'-]*)\\s+off\\b|flip\\s+(?:off\\s+)?the\\s+bird\\b`
            + `|give\\s+${WHO}\\s+the\\s+(?:finger|bird)\\b|(?:show|give)\\s+${WHO}\\s+(?:your\\s+|the\\s+)?middle\\s+finger\\b`,
        anywhere: [
            /\bflip\s+(?:me|us)\s+off\b/,
            /\bgive\s+(?:me|us)\s+the\s+(?:finger|bird)\b/,
            /\bflip\s+(?:off\s+)?the\s+bird\b/,
            new RegExp(`\\bmiddle\\s+finger\\s+(?:at|to)\\s+${WHO}\\b`),
            // Said TO the companion, it answers in kind (owner, 2026-11-04: "flip off whenever it hears 'fuck you'").
            // Speech-to-text often censors it ("f*** you", "f*ck you"), so those count too. "What the fuck you doing"
            // is not aimed at anyone, so "the" right before it does not count.
            /(?<!\bthe\s)\bf(?:uck|\*+ck|u\*+|\*+k|\*+)\s+(?:you|u|ya|yall|y'all|off)\b/,
        ],
        hints: ['flip', 'finger', 'bird', 'fuck', 'f*'],
    },
    facepalm: {
        // "your" is optional on both sides: speech-to-text drops it ("put hand to face").
        imperative: '(?:do\\s+a\\s+)?face\\s?-?palm\\b'
            + `|give\\s+${WHO}\\s+a\\s+(?:big\\s+)?face\\s?-?palm\\b`
            + '|put\\s+(?:your\\s+)?(?:hands?|palms?)\\s+(?:on|over|to)\\s+(?:your\\s+)?(?:head|face|forehead)\\b'
            + '|(?:act|look)\\s+(?:so\\s+|really\\s+)?embarrassed\\b|hide\\s+your\\s+face\\b',
        anywhere: [/\bdo\s+a\s+face\s?-?palm\b/],
        hints: ['palm', 'embarrassed', 'hide your face', 'head', 'face', 'forehead'],
    },
    furious: {
        imperative: '(?:get|go)\\s+(?:really\\s+|super\\s+|totally\\s+|so\\s+)?(?:furious|mad|angry|ballistic|nuts)\\b'
            + '|lose\\s+it\\b|freak\\s+out\\b|flip\\s+out\\b|throw\\s+a\\s+(?:fit|tantrum)\\b'
            + '|(?:go|fly)\\s+into\\s+a\\s+(?:rage|fit)\\b'
            + '|show\\s+(?:me|us)\\s+(?:how\\s+)?(?:angry|mad|furious)\\b',
        hints: ['furious', 'mad', 'angry', 'ballistic', 'nuts', 'lose it', 'freak', 'flip out', 'fit', 'tantrum', 'into a'],
    },
    dance: {
        imperative: 'dance\\b(?!\\s+(?:floor|party|music|class|company|studio|club|track|song|moves?\\s+(?:are|were|is)))'
            + '|bust\\s+a\\s+move\\b|show\\s+(?:me|us)\\s+(?:your|some)\\s+(?:dance\\s+)?moves\\b'
            + '|do\\s+(?:a|the)\\s+(?:little\\s+|happy\\s+|victory\\s+)?dance\\b',
        anywhere: [/\bshow\s+(?:me|us)\s+(?:your|some)\s+dance\s+moves\b/, /\bbust\s+a\s+move\b/],
        hints: ['dance', 'move'],
    },
    fall: {
        imperative: 'fall\\s+(?:down|over)\\b|(?:play|drop)\\s+dead\\b|faint\\b|pass\\s+out\\b|collapse\\b'
            + '|hit\\s+the\\s+(?:floor|ground|deck)\\b',
        hints: ['fall', 'dead', 'faint', 'pass out', 'collapse', 'hit the'],
    },
};

const IMPERATIVE: Record<CompanionActionId, RegExp> = Object.fromEntries(
    (Object.keys(PHRASES) as CompanionActionId[]).map((id) => [id, new RegExp(
        // A statement carries on with a verb ("salsa dance is fun", "road rage is real"); a
        // command does not.
        `(?:^|[.!?;]\\s*)(?:(?:hey|yo|ok|okay)\\s+)?(?:([a-z][a-z'-]*)\\s*,?\\s+)?${LEAD_IN}(?:${PHRASES[id].imperative})`
        + '(?!\\s+(?:is|was|are|were|has|have|had|will|would|can|could)\\b)',
    )]),
) as Record<CompanionActionId, RegExp>;

function normalise(text: string): string {
    return text.toLowerCase().replace(/[‘’]/g, "'").replace(/\s+/g, ' ').trim();
}

const asName = (w: string | undefined): string | null =>
    w && !NOT_A_NAME.has(w) && !NEGATION.test(w) ? w : null;

/** Leading vocative before the verb ("hey Jen, can you …"), or a trailing one ("…, Jen"). */
function vocative(t: string): string | null {
    // "hey Jen" names Jen; "ok wave" names nobody, so after ok/okay a name needs its comma.
    const lead = /\b(?:hey|yo)\s+([a-z][a-z'-]*)\b/.exec(t)?.[1]
        ?? /\b(?:ok|okay)\s+([a-z][a-z'-]*)\s*,/.exec(t)?.[1];
    const trail = /,\s*([a-z][a-z'-]*)\s*[.!?]*$/.exec(t)?.[1];
    return asName(lead) ?? asName(trail);
}

/** The clause of `t` containing index `at`. */
function clauseAt(t: string, at: number): string {
    const start = Math.max(t.lastIndexOf('.', at - 1), t.lastIndexOf('!', at - 1), t.lastIndexOf('?', at - 1),
        t.lastIndexOf(';', at - 1)) + 1;
    return t.slice(start, at);
}

/**
 * The body-action command in an utterance, or null. Only imperatives count: "wave at me",
 * "Jen, flip Ken off", "fall down" do; "a heat wave", "they danced", "I almost fell over",
 * "don't dance" do not. The first action in catalog order wins if one utterance has two.
 */
export function matchCompanionActionCommand(text: string): CompanionActionCommand | null {
    const t = normalise(text);
    if (!t) return null;
    for (const action of Object.keys(PHRASES) as CompanionActionId[]) {
        const p = PHRASES[action];
        if (!p.hints.some((h) => t.includes(h))) continue;
        const m = IMPERATIVE[action].exec(t);
        if (m) {
            const lead = m[0].slice(0, m[0].length - (m[0].match(new RegExp(`(?:${p.imperative})$`))?.[0].length ?? 0));
            // "a fall down the stairs", "the dance", "my wave": an article makes it a noun.
            if (!NEGATION.test(lead) && !NEGATION.test(m[1] ?? '') && !DETERMINER.has(m[1] ?? '')) {
                return { action, addressee: asName(m[1]) ?? vocative(t) };
            }
        }
        for (const re of p.anywhere ?? []) {
            const a = re.exec(t);
            if (a && !NEGATION.test(clauseAt(t, a.index))) return { action, addressee: vocative(t) };
        }
    }
    return null;
}

/**
 * Whether a command is meant for the companion called `names` (display name, and any short forms).
 * Unaddressed commands are for everyone; addressed ones only for a companion whose name matches
 * a word of it ("Jen" matches "Jen Park").
 */
export function commandTargetsCompanion(cmd: CompanionActionCommand, names: string[]): boolean {
    if (!cmd.addressee) return true;
    const words = names.flatMap((n) => normalise(n).split(/[\s_-]+/)).filter(Boolean);
    return words.some((w) => soundsLike(cmd.addressee!, w));
}

/** Spelling a speech-to-text vendor might give a name: doubled letters and a soft ending dropped. */
const nameKey = (w: string): string => w.replace(/(.)\1+/g, '$1').replace(/(?:ie|ey|y|e|i)$/, '');

/**
 * Whether a heard name is the companion's name. Speech-to-text spells names its own way: "Telly"
 * arrives as "Tele" or "Kelly" (panda, 2026-11-04), and an exact match dropped those commands
 * silently. Same key after nameKey, or for names of five letters or more one edit apart; short
 * names stay exact, so "Jen" never answers to "Ken".
 */
function soundsLike(heard: string, name: string): boolean {
    if (heard === name || nameKey(heard) === nameKey(name)) return true;
    return heard.length >= 5 && name.length >= 5 && editDistanceAtMost1(heard, name);
}

function editDistanceAtMost1(a: string, b: string): boolean {
    if (Math.abs(a.length - b.length) > 1) return false;
    let i = 0, j = 0, edits = 0;
    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) { i++; j++; continue; }
        if (++edits > 1) return false;
        if (a.length > b.length) i++;
        else if (a.length < b.length) j++;
        else { i++; j++; }
    }
    return edits + (a.length - i) + (b.length - j) <= 1;
}
